import type { TabMessage, AnnounceMsg } from "./protocol"

export type PeerInfo = {
  target: string
  href: string
  ts: number
}

const ROLL_CALL_SETTLE_MS = 100

export class TabRegistry extends EventTarget {
  private _self: string | undefined
  private _href: string | undefined
  private _ts: number | undefined
  private _peers = new Map<string, PeerInfo>()
  private _windowRefs = new Map<string, Window>()
  private _channel: BroadcastChannel
  private _window: Window

  constructor(options?: { channel?: BroadcastChannel; window?: Window }) {
    super()
    this._channel = options?.channel ?? new BroadcastChannel("spaestiem-tabs")
    this._window = options?.window ?? window

    this._channel.addEventListener("message", this._onMessage)
  }

  get self(): string | undefined {
    return this._self
  }

  get peers(): ReadonlyMap<string, PeerInfo> {
    return this._peers
  }

  hasPeer(target: string): boolean {
    return this._peers.has(target)
  }

  async join(target: string, href: string): Promise<void> {
    this._self = target
    this._href = href
    this._ts = Date.now()

    // Ask existing tabs to re-announce
    this._post({ type: "roll-call" })

    // Announce ourselves
    this._post({
      type: "announce",
      target,
      href,
      ts: this._ts,
    })

    // Register unload handler
    this._window.addEventListener("pagehide", this._onUnload)

    // Wait for roll-call responses to settle
    await new Promise<void>((r) => setTimeout(r, ROLL_CALL_SETTLE_MS))
  }

  openOrFocus(href: string, target: string, features?: string): void {
    // Fast path: stored reference in same browsing context group
    const known = this._windowRefs.get(target)
    if (known && !known.closed) {
      known.focus()
      return
    }
    if (known?.closed) this._windowRefs.delete(target)

    // Medium path: peer is known via BC but no window reference.
    // Use window.open("", target) to get a reference to the existing
    // window without navigating it (empty URL = no reload), then focus.
    // This works because we're in a click handler (user-gesture context),
    // unlike BroadcastChannel-based focus which browsers block.
    // Features are intentionally omitted: re-focusing an existing window
    // must not try to resize/re-chrome it.
    if (this._peers.has(target)) {
      const win = this._window.open("", target)
      if (win) {
        win.focus()
        this._windowRefs.set(target, win)
      }
      return
    }

    // Slow path: open a new window. Features (e.g. "popup=yes") only apply
    // when the window is being created for the first time.
    const win = features
      ? this._window.open(href, target, features)
      : this._window.open(href, target)
    if (!win) return
    this._windowRefs.set(target, win)
  }

  setWindowRef(target: string, win: Window): void {
    this._windowRefs.set(target, win)
  }

  destroy(): void {
    this._channel.removeEventListener("message", this._onMessage)
    this._channel.close()
    this._window.removeEventListener("pagehide", this._onUnload)
  }

  private _post(msg: TabMessage): void {
    try {
      this._channel.postMessage(msg)
    } catch {
      // Channel may be closed
    }
  }

  private _onMessage = (e: MessageEvent): void => {
    const msg = e.data as TabMessage
    if (!msg?.type) return

    switch (msg.type) {
      case "announce":
        this._handleAnnounce(msg as AnnounceMsg)
        break
      case "depart":
        this._peers.delete(msg.target)
        this.dispatchEvent(
          new CustomEvent("peer-departed", {
            detail: { target: msg.target },
          }),
        )
        break
      case "roll-call":
        if (this._self != null && this._ts != null && this._href != null) {
          this._post({
            type: "announce",
            target: this._self,
            href: this._href,
            ts: this._ts,
          })
        }
        break
      case "focus":
        if (msg.target === this._self) {
          this._window.focus()
        }
        break
    }
  }

  private _handleAnnounce(msg: AnnounceMsg): void {
    if (msg.target === this._self) {
      // Duplicate: same target name as us
      if (this._ts != null && msg.ts > this._ts) {
        // The other instance is newer — we should yield
        this.dispatchEvent(new CustomEvent("duplicate-self", { detail: msg }))
      }
      // If we're newer (or equal), ignore — the other will yield
      return
    }

    // Track as a peer
    this._peers.set(msg.target, {
      target: msg.target,
      href: msg.href,
      ts: msg.ts,
    })
    this.dispatchEvent(
      new CustomEvent("peer-joined", {
        detail: { target: msg.target, href: msg.href, ts: msg.ts },
      }),
    )
  }

  private _onUnload = (): void => {
    if (this._self) {
      this._post({ type: "depart", target: this._self })
    }
  }
}
